import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
    Box,
    Button,
    Flex,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { getAttemptGradeSyncLogs } from '@/features/Quiz/api/getQuizzes';
import { GradeSyncLogErrorDetailPanel } from '@/features/Quiz/components/GradeSyncLogsModal/GradeSyncLogErrorDetailPanel';
import {
    extractGradeSyncLogSummary,
    formatGradeSyncLogFullMessage,
    shouldShowFullErrorDetail,
} from '@/features/Quiz/components/GradeSyncLogsModal/gradeSyncLogMessage';

export type GradeSyncLogsModalContext = {
    attemptUuid: string;
    quizTitle: string;
    attemptNumber?: number | null;
    score?: string | null;
    percentage?: string | null;
    fallbackEmail?: string | null;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    context: GradeSyncLogsModalContext | null;
};

function formatTimestamp(value?: string | null): string {
    if (!value) return '—';
    try {
        return format(new Date(value), 'MM/dd/yyyy hh:mm a');
    } catch {
        return value;
    }
}

export const GradeSyncLogsModal = ({ isOpen, onClose, context }: Props) => {
    const attemptUuid = context?.attemptUuid ?? '';
    const [errorPanelOpen, setErrorPanelOpen] = useState(false);
    const [selectedLogMessage, setSelectedLogMessage] = useState<string | null>(null);
    const [selectedLogStatus, setSelectedLogStatus] = useState<string | null>(null);
    const [selectedLogTimestamp, setSelectedLogTimestamp] = useState<string | null>(null);

    const { data, isLoading, isError } = useQuery(
        ['attempt-grade-sync-logs', attemptUuid],
        () => getAttemptGradeSyncLogs(attemptUuid),
        {
            enabled: isOpen && !!attemptUuid,
            useErrorBoundary: false,
        }
    );

    useEffect(() => {
        if (!isOpen) {
            setErrorPanelOpen(false);
            setSelectedLogMessage(null);
            setSelectedLogStatus(null);
            setSelectedLogTimestamp(null);
        }
    }, [isOpen]);

    const studentEmail =
        data?.user?.email?.trim() ||
        context?.fallbackEmail?.trim() ||
        '—';

    const scoreLabel = context?.score ?? '—';
    const percentageLabel = context?.percentage != null ? `${context.percentage}%` : '—';

    const handleOpenErrorPanel = (message: string | null | undefined, status?: string | null, timestamp?: string | null) => {
        setSelectedLogMessage(message ?? null);
        setSelectedLogStatus(status ?? null);
        setSelectedLogTimestamp(timestamp ?? null);
        setErrorPanelOpen(true);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="3xl">
            <ModalOverlay />
            <ModalContent position="relative" overflow="hidden">
                <ModalHeader>Grade Sync Logs</ModalHeader>
                <ModalCloseButton />
                <ModalBody maxH="70vh" overflowY="auto" overflowX="hidden">
                    <Box mb={4} fontSize="14px" color="#202020">
                        <Flex direction="column" gap={1}>
                            <Text>
                                <Text as="span" fontWeight="600">
                                    Student email:{' '}
                                </Text>
                                {studentEmail}
                            </Text>
                            <Text>
                                <Text as="span" fontWeight="600">
                                    Quiz:{' '}
                                </Text>
                                {context?.quizTitle ?? '—'}
                            </Text>
                            <Text>
                                <Text as="span" fontWeight="600">
                                    Attempt:{' '}
                                </Text>
                                {context?.attemptNumber ?? '—'}
                            </Text>
                            <Text>
                                <Text as="span" fontWeight="600">
                                    Score:{' '}
                                </Text>
                                {scoreLabel}
                                {context?.score != null ? ` (${percentageLabel})` : ''}
                            </Text>
                        </Flex>
                    </Box>

                    {isLoading ? (
                        <Flex justify="center" py={8}>
                            <Spinner />
                        </Flex>
                    ) : isError ? (
                        <Text fontSize="14px" color="red.500" textAlign="center" py={4}>
                            Failed to load grade sync logs.
                        </Text>
                    ) : !data?.logs?.length ? (
                        <Text fontSize="14px" color="#666" textAlign="center" py={4}>
                            No grade sync logs available for this attempt.
                        </Text>
                    ) : (
                        <Table size="sm" variant="simple" tableLayout="fixed" width="100%">
                            <Thead>
                                <Tr>
                                    <Th width="18%">Timestamp</Th>
                                    <Th width="14%">Status</Th>
                                    <Th width="10%">Status code</Th>
                                    <Th width="58%">Message</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {data.logs.map((log, index) => {
                                    const summary = extractGradeSyncLogSummary(log.message);
                                    const showFullError = shouldShowFullErrorDetail(log.message);

                                    return (
                                        <Tr key={`${log.timestamp ?? 'log'}-${index}`}>
                                            <Td verticalAlign="top">{formatTimestamp(log.timestamp)}</Td>
                                            <Td verticalAlign="top">{log.status ?? '—'}</Td>
                                            <Td verticalAlign="top">{log.statusCode ?? '—'}</Td>
                                            <Td verticalAlign="top" wordBreak="break-word" overflowWrap="anywhere">
                                                <Text fontSize="14px" noOfLines={2}>
                                                    {summary}
                                                </Text>
                                                {showFullError && (
                                                    <Link
                                                        fontSize="13px"
                                                        color="quiz.primaryBlue"
                                                        fontWeight="500"
                                                        mt={1}
                                                        display="inline-block"
                                                        onClick={() =>
                                                            handleOpenErrorPanel(
                                                                log.message,
                                                                log.status,
                                                                log.timestamp
                                                            )
                                                        }>
                                                        View full error
                                                    </Link>
                                                )}
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={onClose} borderRadius="40px">
                        Close
                    </Button>
                </ModalFooter>

                <GradeSyncLogErrorDetailPanel
                    isOpen={errorPanelOpen}
                    onClose={() => setErrorPanelOpen(false)}
                    fullMessage={formatGradeSyncLogFullMessage(selectedLogMessage)}
                    status={selectedLogStatus}
                    timestampLabel={formatTimestamp(selectedLogTimestamp)}
                />
            </ModalContent>
        </Modal>
    );
};
